import { useRef, useEffect } from 'react';
import {
  BellIcon,
  Cog6ToothIcon,
  QuestionMarkCircleIcon
} from '@heroicons/react/24/outline';
import Button from '../../ui/Button';
import { NavMenuItems } from '../../menu/MenuComponents';
import BrandLogo from '../../../features/branding/BrandLogo';
import { ROUTES } from '../../../utils/constants';
import { dashboardPathForRole } from '../../../routes/dashboardRouteContract';
import { useNavigate } from 'react-router-dom';
import UserAccountMenu from './UserAccountMenu';
import NotificationTray from '../../../features/notifications/components/NotificationTray';
import { useSchool } from '../../../contexts/SchoolContext';
import { useParentMessages } from '../../../features/messaging/hooks/useParentMessages';
import { useToast } from '../../feedback/ToastSystem';
import LearnerNavSearch from './LearnerNavSearch';

const NavBar = ({ user, onLogout, currentRole, canSwitchRoles, availableRoles, switchRole, currentPermissions, menuItems }) => {
  const navigate = useNavigate();
  const { school } = useSchool();
  const isParent = currentRole === 'parent' || user?.isParent;
  const { unreadCount, messages, loading: messagesLoading } = useParentMessages({ autoSubscribe: isParent });
  const { showInfo } = useToast();
  const prevUnreadRef = useRef(null);

  // Toast when new unread messages arrive (not on initial load)
  // Wait for listener to finish loading before establishing baseline
  // to avoid re-toasting old unread messages on every mount
  useEffect(() => {
    if (!isParent || messagesLoading) return;
    if (prevUnreadRef.current === null) {
      // First real data from listener — record baseline, don't toast
      prevUnreadRef.current = unreadCount;
      return;
    }
    if (unreadCount > prevUnreadRef.current) {
      const newCount = unreadCount - prevUnreadRef.current;
      const newest = messages.find(m => !m.delivery?.inApp?.readAt);
      const subject = newest?.content?.subject || 'New message';
      showInfo(subject, `You have ${newCount} new ${newCount === 1 ? 'message' : 'messages'}`, 6000);
    }
    prevUnreadRef.current = unreadCount;
  }, [isParent, messages, messagesLoading, showInfo, unreadCount]);

  const handleLogoClick = () => {
    navigate(dashboardPathForRole(currentRole));
  };

  return (
    <div className="bg-base-100 border-b border-base-300 shadow-sm fixed top-0 left-0 right-0 z-50">
      <div className="navbar h-20 px-4 sm:px-6 lg:px-8">
        <div className="flex-none">
          <button
            onClick={handleLogoClick}
            className="flex items-center hover:opacity-80 transition-opacity cursor-pointer bg-transparent border-none p-0"
          >
            <BrandLogo />
          </button>
        </div>

        <div className="flex-1 hidden lg:flex justify-center min-w-0 px-8">
          <NavMenuItems menuItems={menuItems || []} />
        </div>

        <div className="flex-none ml-auto">
          {user ? (
            <div className="flex items-center gap-1.5">
              {currentRole === 'parent' ? (
                <div className="hidden md:flex items-center px-2 py-2 bg-base-200 rounded-lg">
                  <div className="text-sm">
                    <div className="font-medium text-base-content">
                      {school?.name || user.displayName || 'User'}
                    </div>
                    <div className="text-xs text-base-content/60">
                      {user.email}
                    </div>
                  </div>
                </div>
              ) : (
                <LearnerNavSearch currentRole={currentRole} />
              )}

              {/* Show NotificationTray for staff, message bell for parents */}
              {currentRole === 'parent' ? (
                <button
                  className="p-2 rounded-lg transition-colors relative shadow-none"
                  onClick={() => navigate(ROUTES.MESSAGES)}
                  aria-label={unreadCount > 0 ? `${unreadCount} unread messages` : 'Messages'}
                >
                  <BellIcon className="w-5 h-5 hover:scale-110 transition-transform" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center bg-error text-error-content text-[10px] font-bold rounded-full px-1">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </button>
              ) : (
                <NotificationTray />
              )}

              <button
                className="p-2 rounded-lg transition-colors shadow-none hover:bg-base-200"
                onClick={() => navigate(ROUTES.HELP_CENTER)}
                aria-label="Help Center"
                title="Help Center"
              >
                <QuestionMarkCircleIcon className="w-5 h-5 hover:scale-110 transition-transform" />
              </button>

              {currentRole === 'admin' && (
                <button
                  className="p-2 rounded-lg transition-colors shadow-none hover:bg-base-200"
                  onClick={() => navigate(ROUTES.SETUP)}
                  aria-label="School Setup"
                  title="School Setup"
                  data-testid="school-setup-shortcut"
                >
                  <Cog6ToothIcon className="w-5 h-5 hover:scale-110 transition-transform" />
                </button>
              )}

              <UserAccountMenu
                user={user}
                onLogout={onLogout}
                currentRole={currentRole}
                canSwitchRoles={canSwitchRoles}
                availableRoles={availableRoles}
                switchRole={switchRole}
                currentPermissions={currentPermissions}
              />
            </div>
          ) : (
            <div className="flex items-center space-x-3">
              <Button variant="ghost" onClick={() => navigate(ROUTES.LOGIN)}>
                Sign In
              </Button>
              <Button variant="primary" onClick={() => navigate(ROUTES.WELCOME)}>
                Get Started
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NavBar;
